import { useEffect, useState } from "react";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";
import { estadoInicialFiltroProyecto, type IEntidadesRegistroCliente, type IModalproyectoCVProps } from "../interfaces";
import ModalProyectoVista from "../vista/modalProyectoVista";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useDebouncedValue } from "@mantine/hooks";
import { useProyecto } from "../contextoProyecto";
import { ProveedorCargando, useCargando } from "../../../globales/contextos/cargandoContext";
import { RegionalModelo } from "../../../modelos/Regionales";
import { UbicacionModelo } from "../../../modelos/Ubicacion";
import { ZonaModelo } from "../../../modelos/Zonas";

const ModalproyectoCV: React.FunctionComponent<IModalproyectoCVProps> = ({
    consultarProyectos,
    abrirModalProyecto,
    estadoModal
}) => {
    const modeloProyecto = new ProyectoModelo();
    const modeloRegional = new RegionalModelo();
    const modeloUbicacion = new UbicacionModelo();
    const modeloZona = new ZonaModelo();

    // ✅ Estado para saber si Google Maps está disponible
    const [isGoogleReady, setIsGoogleReady] = useState(false);

    useEffect(() => {
        const apiKey = import.meta.env.VITE_GOOGLE_API_KEY || "";

        // Si ya está cargado, no vuelvas a insertarlo
        if (window.google?.maps) {
            setIsGoogleReady(true);
            return;
        }

        // Revisa si ya hay un script cargándose
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
        if (existingScript) {
            existingScript.addEventListener("load", () => setIsGoogleReady(true));
            return;
        }

        // Carga el script solo una vez
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setIsGoogleReady(true);
        script.onerror = () => console.error("Error cargando Google Maps API");
        document.head.appendChild(script);
    }, []);


    const { mostrarNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();
    const [errores, setErrores] = useState<Partial<Record<keyof IProyecto, string | null>>>({});
    const { proyecto, setProyecto } = useProyecto();
    const [entidadesProyecto, setEntidadesProyecto] = useState<IEntidadesRegistroCliente>({
        Paises: [],
        Regionales: [],
        Zonas: [],
        Ciudades: [],
    });

    const [debouncedLatitud] = useDebouncedValue(proyecto.Latitud, 400);
    const [debouncedLongitud] = useDebouncedValue(proyecto.Longitud, 400);
    const [origen, setOrigen] = useState<"direccion" | "coordenadas" | null>(null);
    // Flags para asegurar que las búsquedas de geocodificación
    // solo ocurran cuando el usuario interactúa (onChange) y no al abrir
    // el modal para editar un proyecto ya existente.
    const [userEditedCoordenadas, setUserEditedCoordenadas] = useState(false);

    // Cache para almacenar las coordenadas de las direcciones ya consultadas
    // const direccionesCache = useRef<Map<string, { lat: string; lon: string }>>(new Map());


    const regexLat = /^-?(?:[0-8]?\d(\.\d+)?|90(\.0+)?)$/;
    const regexLng = /^-?(?:1[0-7]\d(\.\d+)?|180(\.0+)?|[0-9]?\d(\.\d+)?|1[0-7]\d(\.\d+)?)$/;

    useEffect(() => {
        if (proyecto.PaisId !== 0) {
            consultarRegionales(proyecto.PaisId);
            consultarCiudades(proyecto.PaisId)
        }
    }, [proyecto.PaisId])

    useEffect(() => {
        if (estadoModal) {
            consultarPaises();
            consultarZonas();
            // Reiniciar flags de búsqueda para evitar consultas automáticas
            // cuando se abre el modal para edición (los valores vienen de DB).
            setUserEditedCoordenadas(false);
        }
        else {
            setErrores({})
        }
    }, [estadoModal])


    // ✅ Solo ejecuta reverse-geocoding si el usuario modificó ambas coordenadas
    useEffect(() => {
        if (
            origen === "coordenadas" &&
            userEditedCoordenadas &&
            regexLat.test(proyecto.Latitud.toString()) &&
            regexLng.test(proyecto.Longitud.toString()) &&
            debouncedLatitud &&
            debouncedLongitud &&
            isGoogleReady
        ) {
            consultarPorCoordenadas(Number(debouncedLatitud), Number(debouncedLongitud));
        }
    }, [
        debouncedLatitud,
        debouncedLongitud,
        origen,
        userEditedCoordenadas,
        isGoogleReady,
        regexLat,
        regexLng,
    ]);



    const consultarPaises = async () => {
        try {
            manejarCargando(true, undefined, true);
            const paises = await modeloUbicacion.obtenerPaises();
            setEntidadesProyecto((prev) => ({ ...prev, Paises: paises.Data }));

        } catch {
            setEntidadesProyecto({
                ...entidadesProyecto,
                Paises: []
            })
        } finally {
            manejarCargando(false);
        }

    }

    const consultarRegionales = async (PaisId: number) => {
        try {
            manejarCargando(true, undefined, true);
            const listaRegionalesTotal = await modeloRegional.obtenerListaRegionales();
            const regionalesFltradasPais = listaRegionalesTotal.Data.filter(r => r.PaisId === PaisId)
            setEntidadesProyecto((prev) => ({ ...prev, Regionales: regionalesFltradasPais }));

        } finally {
            manejarCargando(false);
        }
    }

    const consultarZonas = async () => {
        try {
            manejarCargando(true, undefined, true);
            const listaZonas = await modeloZona.obtenerListaZonas();
            setEntidadesProyecto((prev) => ({ ...prev, Zonas: listaZonas.Data }));

        } finally {
            manejarCargando(false);
        }
    }

    const consultarCiudades = async (PaisId: number) => {
        try {
            manejarCargando(true, undefined, true);
            const listaCiudadesTotal = await modeloUbicacion.obtenerCiudades();
            const ciudadesFiltradasPais = listaCiudadesTotal.Data.filter(c => c.PaisId === PaisId)
            setEntidadesProyecto((prev) => ({ ...prev, Ciudades: ciudadesFiltradasPais }))
        } finally {
            manejarCargando(false);
        };
    }

    const consultarPorCoordenadas = async (latitud: number, longitud: number) => {
        if (!isGoogleReady || !userEditedCoordenadas) return;
        if (!regexLat.test(latitud.toString()) || !regexLng.test(longitud.toString())) return;

        try {
            const geocoder = new google.maps.Geocoder();
            const { results, status } = await new Promise<{
                results: google.maps.GeocoderResult[];
                status: google.maps.GeocoderStatus;
            }>((resolve) => {
                geocoder.geocode({ location: { lat: latitud, lng: longitud } }, (res, stat) => {
                    resolve({ results: res || [], status: stat });
                });
            });

            if (status === "OK" && results.length > 0) {
                // ✅ Setear solo la dirección (sin volver a ejecutar por cambio de coordenadas)
                setUserEditedCoordenadas(false);
                setProyecto((prev) => ({
                    ...prev,
                    Direccion: results[0].formatted_address || "",
                }));
            }
        } catch (error) {
            console.error("Error en reverse geocoding:", error);
        }
    };





    const validarCamposProyecto = () => {
        let esValido = true;
        let coordenadasValidas = true

        if (!proyecto.Nombre.trim()) {
            agregarError("Nombre", "Este campo es requerido");
            esValido = false;
        }

        if (!proyecto.NitProyecto.trim()) {
            agregarError("NitProyecto", "Este campo es requerido");
            esValido = false;
        }

        if (proyecto.PaisId === 0) {
            agregarError("PaisId", "Selecciona un país");
            esValido = false;
        }

        // if (proyecto.DepartamentoEstadoId === 0) {
        //   agregarError("DepartamentoEstadoId", "Selecciona un departamento");
        //   esValido = false;
        // }


        if (!proyecto.Direccion.trim()) {
            agregarError("Direccion", "Este campo es requerido");
            esValido = false;
        }

        if (!proyecto.Latitud.toString().trim()) {
            agregarError("Latitud", "Este campo es requerido");
            esValido = false;
        }

        if (!proyecto.Longitud.toString().trim()) {
            agregarError("Longitud", "Este campo es requerido");
            esValido = false;
        }

        if ((proyecto.Longitud !== "" &&
            !regexLng.test(proyecto.Longitud.toString())) && !(proyecto.Latitud !== "" &&
                !regexLat.test(proyecto.Latitud.toString()))) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Coordenadas invalidas",
                "Longitud inválida (rango -180 a 180)",
                5000
            );
            coordenadasValidas = false
        }

        if ((proyecto.Latitud !== "" &&
            !regexLat.test(proyecto.Latitud.toString())) && !(proyecto.Longitud !== "" &&
                !regexLng.test(proyecto.Longitud.toString()))) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Coordenadas invalidas",
                "Latitud inválida (rango -90 a 90)",
                5000
            );
            coordenadasValidas = false
        }

        if ((proyecto.Latitud !== "" &&
            !regexLat.test(proyecto.Latitud.toString())) && (proyecto.Longitud !== "" &&
                !regexLng.test(proyecto.Longitud.toString()))) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Coordenadas invalidas",
                "Latitud inválida (rango -90 a 90) y Longitud inválida (rango -180 a 180)",
                5000
            );
            coordenadasValidas = false
        }


        if (proyecto.RegionalId === 0) {
            agregarError("RegionalId", "Selecciona una regional");
            esValido = false;
        }

        if (proyecto.ZonaId === 0) {
            agregarError("ZonaId", "Selecciona una zona");
            esValido = false;
        }

        // if (proyecto.CiudadId === 0) {
        //     agregarError("CiudadId", "Selecciona una ciudad");
        //     esValido = false;
        // }

        if (!esValido) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Por favor completa todos los campos antes de continuar.",
                5000
            );
        }

        return esValido && coordenadasValidas;
    };


    const crearProyecto = async () => {
        if (!validarCamposProyecto()) return
        try {
            manejarCargando(true, "Guardando proyecto...", true);
            proyecto.Latitud = Number(proyecto.Latitud)
            proyecto.Longitud = Number(proyecto.Longitud)
            const proyectoRespuesta = await modeloProyecto.crearProyecto({
                ...proyecto,
                Cliente: undefined,
                Contrato: undefined,
                Pais: undefined,
                Regional: undefined,
                Zona: undefined,
                Ciudad: undefined
            });
            if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success && proyectoRespuesta.Data) {
                consultarProyectos(estadoInicialFiltroProyecto)
                setProyecto({ ...proyecto, Id: proyectoRespuesta.Data.Id })
            }
            if (proyectoRespuesta.Messages.length > 0) {
                proyectoRespuesta.Messages.forEach(mensaje =>
                    mostrarNotificacion(
                        proyectoRespuesta.NotificationType,
                        '',
                        mensaje,
                        4000
                    ));
            }

        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al crear proyecto',
                'No fue posible crear el proyecto. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const editarProyecto = async () => {
        try {
            manejarCargando(true, undefined, true);
            proyecto.Latitud = Number(proyecto.Latitud)
            proyecto.Longitud = Number(proyecto.Longitud)
            proyecto.Eliminado = false
            proyecto.Activo = true
            const proyectoRespuesta = await modeloProyecto.editarProyecto({
                ...proyecto,
                Cliente: undefined,
                Contrato: undefined,
                Pais: undefined,
                Regional: undefined,
                Zona: undefined,
                Ciudad: undefined
            });
            if (proyectoRespuesta.NotificationType === EnumTipoNotificacion.Success) {
                consultarProyectos(estadoInicialFiltroProyecto)
            }
            if (proyectoRespuesta.Messages.length > 0) {
                proyectoRespuesta.Messages.forEach(mensaje =>
                    mostrarNotificacion(
                        proyectoRespuesta.NotificationType,
                        '',
                        mensaje,
                        4000
                    ));
            }

        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al actualizar proyecto',
                'No fue posible actualizar el proyecto. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const guardarEditarProyecto = () => {
        if (proyecto.Id === 0) {
            crearProyecto();
        }
        else {
            editarProyecto();
        }
    }

    const manejarCambioValorProyecto = async (campo: keyof IProyecto, valor: IProyecto[keyof IProyecto]) => {
        if (campo === 'PaisId') {
            if (valor !== 0 && valor) {
                setProyecto({
                    ...proyecto,
                    RegionalId: 0,
                    CiudadId: 0,
                    Direccion: ''
                })
                setEntidadesProyecto({ ...entidadesProyecto, Regionales: [], Ciudades: [] })

                await consultarRegionales(Number(valor))
                await consultarCiudades(Number(valor))
            }
            else {
                setProyecto({
                    ...proyecto,
                    RegionalId: 0,
                    CiudadId: 0,
                    Direccion: ''
                })
                setEntidadesProyecto({ ...entidadesProyecto, Regionales: [], Ciudades: [] })
            }
        }

        // if (campo === 'Direccion') {
        //     const direccion = valor?.toString() || '';

        //     // Si la dirección está en el cache, usar esas coordenadas
        //     const coordenadasCached = direccionesCache.current.get(direccion);
        //     if (coordenadasCached) {
        //         setProyecto(prev => ({
        //             ...prev,
        //             [campo]: direccion,
        //             Latitud: coordenadasCached.lat,
        //             Longitud: coordenadasCached.lon
        //         }));
        //         return;
        //     }

        //     // Si está en los resultados actuales, guardar en cache y usar
        //     const direccionDatos = entidadesProyecto.ResultadosDirecciones.find(rd => rd.display_name === direccion);
        //     if (direccionDatos) {
        //         // Guardar en cache
        //         direccionesCache.current.set(direccion, {
        //             lat: direccionDatos.lat,
        //             lon: direccionDatos.lon
        //         });

        //         setProyecto(prev => ({
        //             ...prev,
        //             [campo]: direccion,
        //             Latitud: direccionDatos.lat,
        //             Longitud: direccionDatos.lon
        //         }));
        //         return;
        //     }

        //     // Si no está en cache ni en resultados actuales, solo actualizar la dirección
        //     setProyecto(prev => ({
        //         ...prev,
        //         [campo]: direccion
        //     }));
        //     setOrigen('direccion');
        // }

        if (campo === 'Latitud' || campo === 'Longitud') {
            setOrigen('coordenadas')
            // El usuario modificó coordenadas manualmente
            setUserEditedCoordenadas(true);
        }

        setProyecto((prevProyecto) => ({
            ...prevProyecto,
            [campo]: valor
        }));
    }

    const agregarError = async (campo: keyof IProyecto, error: string) => {
        setErrores((prevErrores) => ({
            ...prevErrores,
            [campo]: error
        }));

    }

    return (
        <>
            <ProveedorCargando>
                <ModalProyectoVista
                    abrir={estadoModal}
                    abrirModalProyecto={abrirModalProyecto}
                    guardarEditarProyecto={guardarEditarProyecto}
                    manejarCambioValorProyecto={manejarCambioValorProyecto}
                    entidadesRegistro={entidadesProyecto}
                    agregarError={agregarError}
                    errores={errores}
                    regexLat={regexLat}
                    regexLng={regexLng}
                />
            </ProveedorCargando>
        </>
    )
}

export default ModalproyectoCV;